/**
 * @file useSiteDraftParts.ts
 * @description Части черновика вида сайта с автосохранением — одно
 * состояние на весь каркас (phase19.16 Р1; подготовка к оболочке «Сайт →
 * Конструктор», фаза §7.14): оформление, бренд, лента блога, каталог
 * (Р2а), счётчики и cookie (Р3г, `useCountersDraftState`), документ
 * навигации части MENUS («SEO-техника» v0.19.65, `useNavigationDraftState`). SEO с Р3в — не
 * часть черновика (SB145, раздел «Сайт → SEO», `useShopSeoSettings`).
 *
 * Создаётся в `useSiteViewState` (каркас), панели читают через
 * `useSiteDraftPart(…)`. Меню — отдельным состоянием (`useSiteMenus`): у
 * него свои маршруты сервера по пунктам.
 */

import { useContext } from "react";

import { useBlogDraftState, useBrandDraftState, useCatalogDraftState, type PartFieldsApi } from "./usePartFields";
import { useCountersDraftState, type CountersDraftApi } from "./useCountersDraft";
import { useNavigationDraftState, type NavigationDraftApi } from "./useNavigationDraft";
import { useThemeDraftState, type ApplyPart, type ThemeDraftApi } from "./useThemeDraft";
import { SiteViewContext } from "../../components/site-view/site-view-context";
import type { SiteBlogDraft, SiteBrandDraft, SiteCatalogDraft, SiteViewDraft } from "../../lib/site-view/types";

export interface SiteDraftParts {
  theme: ThemeDraftApi;
  brand: PartFieldsApi<SiteBrandDraft>;
  blog: PartFieldsApi<SiteBlogDraft>;
  /** Р2а: вид каталога и карточки товара. */
  catalog: PartFieldsApi<SiteCatalogDraft>;
  /** Р3г: счётчики и режим согласия посетителя. */
  counters: CountersDraftApi;
  /** v0.19.65 (SB146): «Показывать хлебные крошки» — документ навигации части MENUS. */
  navigation: NavigationDraftApi;
}

/** Все части; `null`, пока черновик не загружен. */
export function useSiteDraftParts(draft: SiteViewDraft | null, applyPart: ApplyPart): SiteDraftParts | null {
  const theme = useThemeDraftState(draft, applyPart);
  const brand = useBrandDraftState(draft, applyPart);
  const blog = useBlogDraftState(draft, applyPart);
  const catalog = useCatalogDraftState(draft, applyPart);
  const counters = useCountersDraftState(draft, applyPart);
  const navigation = useNavigationDraftState(draft, applyPart);
  return theme && brand && blog && catalog && counters && navigation ? { theme, brand, blog, catalog, counters, navigation } : null;
}

/**
 * Часть черновика для панели. Панель рисуется после загрузки черновика
 * (панель конструктора ждёт черновик), поэтому часть есть; вне каркаса —
 * ошибка сборки экрана.
 */
export function useSiteDraftPart<K extends keyof SiteDraftParts>(key: K): SiteDraftParts[K] {
  const parts = useContext(SiteViewContext)?.parts;
  if (!parts) throw new Error("useSiteDraftPart(): панель вида сайта рисуется только после загрузки черновика");
  return parts[key];
}
